
import React from 'react';
import {Field} from 'redux-form';

import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';

import {DataMap} from '@grapecity/wijmo.grid';

import {renderTextField, renderSelect, renderCheckbox, renderRadioGroup, renderLink} from '../components/atoms/CustomPart.js';
import {PositiveButton, PositiveButton2} from '../components/atoms/Buttons/PositiveButton.js';
import * as validateRule from '../components/validateRule.js';
import Upload from '../components/organisms/UploadCustom';
import {findIndex, getFilteringMap} from '../common/common.js';
import {CATEGORY_TYPE, ORDER_CATEGORY, ROLE_ID} from '../common/commonConst.js';
import {FormControlLabel, Link, Radio} from '@mui/material';

/**
 * 基本情報表示部の申込基本情報を取得
 * @param {*} userInfo ユーザ情報
 * @param {*} orderData 申込情報
 * @return {*} 基本情報表示部の申込基本情報
 */
export function getOrderInfomationValue(
    userInfo, orderData) {
  let orderInformation = [
    {binding: 'orderNo', name: '申込番号', value: ''},
    {binding: 'rev', name: '版数', value: ''},
    {binding: 'orderKyogasya', name: '共架者コード', value: ''},
    {binding: 'orderZigyosyaName', name: '共架事業者', value: ''},
    {binding: 'orderUketukeKyogasya', name: '申込先共架者コード', value: ''},
    {binding: 'orderUketukeZigyosyaName', name: '申込先共架事業者', value: ''},
    {binding: 'orderDate', name: '申込日', value: '', format: 'date'},
    {binding: 'orderTorokuDate', name: '申込登録日', value: '', format: 'date'},
    {binding: 'kyogaTantoKasyo', name: '共架担当箇所', dataMap: null, value: ''},
    {binding: 'kyogaTantosyaName', name: '共架担当者', value: ''},
    {binding: 'kojiKenmei', name: '工事件名', value: ''},
    {binding: 'kojibasyoZigyojoName', name: '工事場所/事業場', value: ''},
    {binding: 'kojibasyoDaihyoDntNo', name: '工事場所/代表電柱番号', value: ''},
    {binding: 'kojibasyoPrefecturesName', name: '工事場所/都道府県', value: ''},
    {binding: 'kojibasyoMunicipalitiesName', name: '工事場所/市区町村', value: ''},
    {binding: 'kojibasyoOazatusyoName', name: '工事場所/大字通称', value: ''},
    {binding: 'kojibasyoAzatyomeName', name: '工事場所/字丁目', value: ''},
    {binding: 'kojibasyoBirumei', name: '工事場所/ビル名等', value: ''},
    {binding: 'sisetuYearMonth', name: '施設年月', value: '', format: 'month'},
  ];
  const isKyogasya = (userInfo.role_id === ROLE_ID.KYOGASYA);
  const isOrder = (orderData.orderCategory === ORDER_CATEGORY.ORDER);
  const isTasyatyuOrder =
    (orderData.orderCategory === ORDER_CATEGORY.TASYATYU_ORDER);
  if (isKyogasya) {
    // 共架者利用時は非表示
    orderInformation = orderInformation.filter((info) => info.binding != 'kyogaTantoKasyo' && info.binding != 'kyogaTantosyaName');
  }
  if (!isTasyatyuOrder) {
    // 申込区分が「他社柱申込」の場合、表示
    orderInformation = orderInformation.filter((info) => info.binding != 'orderUketukeKyogasya' && info.binding != 'orderUketukeZigyosyaName');
  }
  if (!isOrder && !isTasyatyuOrder) {
    // 申込区分が「共架申込」、「他社柱申込」の場合、表示
    orderInformation = orderInformation.filter((info) => info.binding != 'orderDate' && info.binding != 'orderTorokuDate');
  }
  for (const orderInfo of orderInformation) {
    const binding = orderInfo.binding;
    if (Object.hasOwn(orderData, binding)) {
      orderInfo.value = orderData[binding];
    }
  }
  return orderInformation;
}

export function createFieldItems(gmnItems) {
  let list = [];
  for (const _key of Object.keys(gmnItems)) {
    const gmn = gmnItems[_key];
    if (gmn.label == '') {
      continue;
    }
    let _item = {
      xs: 12, sm: gmn.sm,
      type: gmn.type,
    };
    setItem(gmn, _item);
    list.push(_item);
  }
  return list;
};

export function createGridFields(key, components) {
  const lists = components.map((_item) => {
    let item = {};
    Object.assign(item, _item);
    let component = null;
    const _xs = Object.hasOwn(item, 'xs') ? item.xs : 12;
    const _sm = Object.hasOwn(item, 'sm') ? item.sm : 12;
    delete item.xs;
    delete item.sm;
    if (item.type == 'text' || item.type == 'date' || item.type == 'month') {
      component = (<Grid key={'key' + key + item.name} item xs={_xs} sm={_sm}>
        <Field
          component={renderTextField}
          className="bf-form-field"
          id={'id' + key + item.name}
          {...item}/>
      </Grid>);
    } else if (item.type == 'select') {
      const list = [];
      const selections = item.selections ? item.selections : [];
      let _item = {...item};
      delete _item.type;
      delete _item.blank;
      delete _item.selections;
      if (_item.onChange) {
        item.onChange = _item.onChange;
      }
      if (item.blank) {
        list.push(<MenuItem key={'key' + key + _item.name} value="">&nbsp;</MenuItem>);
      }
      for (const selection of selections) {
        list.push(<MenuItem key={selection.id} value={selection.cd}>
          {selection.cdNameAbbr}</MenuItem>);
      }
      component = (<Grid key={'key' + key + _item.name} item xs={_xs} sm={_sm}>
        <Field
          component={renderSelect}
          className="bf-form-field"
          id={'id' + key + _item.name}
          {..._item}>
          {list}
        </Field>
      </Grid>);
    } else if (item.type == 'positiveButton') {
      const _positiveProps = {
        id: 'id' + item.id,
        onClick: item.click,
        disabled: item.disabled,
        variant: 'contained',
        size: item.size,
        fullWidth: item.fullWidth,
        startIcon: item.startIcon,
      };
      component = (<Grid key={'key' + key + item.id} item xs={_xs} sm={_sm}>
        <PositiveButton
          {..._positiveProps}>
          <span>{item.label}</span>
        </PositiveButton>
      </Grid>);
    } else if (item.type == 'positiveButton2') {
      const _positiveProps = {
        id: 'id' + item.id,
        onClick: item.click,
        disabled: item.disabled,
        variant: 'contained',
        size: item.size,
        fullWidth: item.fullWidth,
        startIcon: item.startIcon,
      };
      component = (<Grid key={'key' + key + item.id} item xs={_xs} sm={_sm}>
        <PositiveButton2
          {..._positiveProps}>
          <span>{item.label}</span>
        </PositiveButton2>
      </Grid>);
    } else if (item.type == 'checkbox') {
      delete item.type;
      component = (<Grid key={'key' + key + item.name} item xs={_xs} sm={_sm}>
        {createCheckBox([item])}
      </Grid>);
    } else if (item.type == 'radiobutton') {
      delete item.type;
      component = (<Grid key={'key' + key + item.name} item xs={_xs} sm={_sm}>
        {createRadioButton([item])}
      </Grid>);
    } else if (item.type == 'fileDL') {
      component = _createFileDownLoad(item);
    } else if (item.type == 'link') {
      component = (<Grid key={'key' + key + item.name} item xs={_xs} sm={_sm}>
        <Field
          component={renderLink}
          className="bf-form-field"
          id={'id' + key + item.name}
          {...item}/>
      </Grid>);
    } else if (item.type == 'simpleLink') {
      component = (
        <Grid key={'key' + key + item.name} item xs={_xs} sm={_sm} style={item.gridStyle}>
          <Link href="#" onClick={(e) => item.click(e)} style={item.linkStyle}>{item.text}</Link>
        </Grid>
      );
    } else if (item.type == 'dummy') {
      component = (<Grid key={'key' + key + item.name} item xs={0} sm={_sm}></Grid>);
    } else {
      // do nothing
    }
    return (component);
  });
  return (<Grid
    container
    justifyContent="flex-start"
    alignItems="flex-start"
    spacing={1}>
    {lists}
  </Grid>);
}

export function createCheckBox(components) {
  const lists = components.map((item) =>{
    let _item = {
      key: 'key' + item.name,
      id: item.name,
      name: item.name,
      disabled: item.disabled,
    };
    if (item.onChange) {
      _item.onChange = item.onChange;
    }
    let component =
    ( <Field
      component={renderCheckbox}
      label={<p style={{maxWidth: '34em'}}>
        {item.label}
      </p>}
      {..._item}
    />);
    return (component);
  });
  return (
    <FormControl component="fieldset">
      <FormGroup>
        {lists}
      </FormGroup>
    </FormControl>);
}
export function createRadioButton(components) {
  const lists = components.map((item) =>{
    let _item = {
      key: 'key' + item.name,
      id: item.name,
      name: item.name,
      disabled: item.disabled,
    };
    if (item.onChange) {
      _item.onChange = item.onChange;
    }

    let component =
    (
      <Field
        component={renderRadioGroup}
        {..._item}
      >
        {item.label.map((label) => {
          return (
            <FormControlLabel
              key={label}
              value={label}
              disabled={item.disabled}
              control={<Radio />}
              label={
                <p style={{color: '#000', maxWidth: '34em', marginBottom: '0'}}>
                  {label}
                </p>}/>);
        })}
      </Field>

    );
    return (component);
  });

  return (
    <FormControl component="fieldset">
      <FormGroup>
        {lists}
      </FormGroup>
    </FormControl>);
}

export function createSimpleGrid(components, nameSize=6, valueSize=6) {
  const lists = components.map((item) =>{
    return (<React.Fragment key={item.name}>
      <Grid item key={item.binding + '1'} xs={nameSize}>
        <Typography gutterBottom>{item.name}</Typography>
      </Grid>
      <Grid item key={item.binding + '2'} xs={valueSize}>
        <Typography gutterBottom className="bf-textWrap">{item.value}</Typography>
      </Grid>
    </React.Fragment>);
  });
  return ( <Grid container>
    {lists}
  </Grid>);
}

export function setSelections(list, label, key, genericMap) {
  const index = findIndex(list,
      (element) => {
        return element.label == label;
      });
  if (index == -1) {
    return;
  }
  list[index].selections = getFilteringMap(genericMap, key);
}

export function setEvent(list, label, event, callback) {
  const index = findIndex(list,
      (element) => {
        return element.label == label;
      });
  if (index == -1) {
    return;
  }
  list[index][event] = callback;
}

export function createFileDownLoad(components) {
  if (!components) {
    return;
  }
  const lists = components.map((item) =>{
    return _createFileDownLoad(item);
  });
  return ( <Grid container>
    {lists}
  </Grid>);
}

function _createFileDownLoad(item) {
  return (
    <Grid item key={item.key} xs={12}>
      <Typography variant="button" display="block" gutterBottom>
        {item.label}
      </Typography>
      <button type="button" className='bf-buttonLink' style={{marginBottom: '1rem'}}
        onClick={() => {
          if (item.onClick) {
            item.onClick();
          }
        }}>
        {item.fileName}
      </button>
    </Grid>);
}

/**
 * ファイルダウンロードの実行.
 *
 * @param {*} response レスポンス
 */
export function executeFileDownload(response) {
  const blob = new Blob([response.data], {
    type: response.data.type,
  });
  // レスポンスヘッダからファイル名を取得する
  const contentDisposition = response.headers['content-disposition'];
  let fileName = contentDisposition.substring(
      contentDisposition.indexOf('*=') + 3,
      contentDisposition.length - 1,
  );
  // +を半角スペースに置換
  fileName = decodeURI(fileName).replace(/\+/g, ' ');
  if (navigator.msSaveBlob) { // IE
    navigator.msSaveBlob(blob, fileName);
  } else {
    let link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', fileName);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export function createUpload(file) {
  return (<Upload
    componentId={file.omponentId}
    maxFileCount={file.maxFileCount}
    maxFileSize={file.maxFileSize}
    required={file.required}
    previewFlag={file.previewFlag}
    previewWidth={file.previewWidth}
    acceptFileType={file.acceptFileType}
    fileSetHandler={file.fileSetHandler}
    initFile={file.initFile}
    disabled={file.disabled}
    customChecks={file.customChecks}
  />);
};

/**
 * 手持箇所名称取得
 * @param {string} orderStatus 申込ステータス
 * @param {array} genericMst 汎用マスタリスト
 * @return {string} 文字列
 */
export function getTemochiKasyo(orderStatus, genericMst) {
  const statusMst = genericMst && (CATEGORY_TYPE.ORDERSTATUS in genericMst) ?
    genericMst[CATEGORY_TYPE.ORDERSTATUS] : [];
  const temochiList = statusMst.filter((data) => data.cd == orderStatus);
  return temochiList.length > 0 ? temochiList[0].valueString3 : '';
};

/**
   * システムエラー
   * @param {*} props
   */
export function sysErr(props) {
  if (!props.doShowMessage) {
    return;
  }
  props.doShowMessage({
    message: 'TKSCS00001S',
    action: () => {
      props.navigate('/login');
    },
  });
}

/**
 * テーブル共通項目設定
 * @deprecated 削除予定。バックエンドの認可を使ってください。
 * @param {string} type insert or update
 * @param {object} values 登録または更新するデータ
 * @param {string} apiId 画面ID
 * @param {string} userId ログインユーザID
 * @param {string} daikoUserId 代行ユーザID
 * @return {object} 共通項目を設定したvalues
 */
export function setCommonColumns(
    type, values, apiId, userId, daikoUserId=null) {
  let _values = {...values};
  if (daikoUserId) {
    _values.daikoUserId = daikoUserId;
  }
  if (type == 'insert') {
    _values.createApid = apiId;
    _values.createUser = userId;
  }
  _values.updateApid = apiId;
  _values.updateUser = userId;
  return _values;
}

/**
 * 申込共架者設定
 * @deprecated 削除予定。バックエンドの認可を使ってください。
 * @param {object} item データ
 * @param {string} companyCd 会社コード
 * @param {string} kyogaZigyosya 事業者コード
 * @param {string} userId ログインユーザID
 */
export function setOrderZigyosya(item, companyCd, kyogaZigyosya, userId) {
  item['orderCompany'] = companyCd;
  item['orderZigyosya'] = kyogaZigyosya;
  item['orderTantosya'] = userId;
}

/**
 * データマップを返す
 * @param {object} genericMap 汎用マスタ
 * @param {string} key バインドキー
 * @return {object} dataMap
 */
export function createDataMap(genericMap, key) {
  const data = [{cd: '', cdNameAbbr: '　'}];
  if (!genericMap) {
    return new DataMap(data, 'cd', 'cdNameAbbr');
  }
  let items = getFilteringMap(genericMap, key);

  let newDataMap = new DataMap(data, 'cd', 'cdNameAbbr');
  if (items) {
    const _newItems = data.concat(items);
    newDataMap = new DataMap(_newItems, 'cd', 'cdNameAbbr');
  }
  return newDataMap;
}

/**
 * データマップを返す
 * @param {object} genericMap 汎用マスタ
 * @param {string} key バインドキー
 * @return {object} dataMap
 */
export function createDataMapNoBlank(genericMap, key) {
  const data = [];
  if (!genericMap) {
    return new DataMap(data, 'cd', 'cdNameAbbr');
  }
  let items = getFilteringMap(genericMap, key);

  let newDataMap = new DataMap(data, 'cd', 'cdNameAbbr');
  if (items) {
    const _newItems = data.concat(items);
    newDataMap = new DataMap(_newItems, 'cd', 'cdNameAbbr');
  }
  return newDataMap;
}

function setValidate(gmn) {
  let _validate = [];
  if (Object.hasOwn(gmn, 'maxLength')) {
    _validate.push(validateRule.maxLength(gmn.maxLength));
  }
  if (gmn.type == 'date') {
    _validate.push(validateRule.date);
  } else if (gmn.type == 'month') {
    _validate.push(validateRule.month);
  }
  if (gmn.dataType == 'decimal') {
    _validate.push(validateRule.decimal(gmn.precision, gmn.scale));
  }
  if (gmn.dataType == 'datetime') {
    _validate.push(validateRule.datetime());
  }
  return _validate;
}
function setWarn(gmn) {
  let _warn = [];
  const required = !!gmn.required;
  if (required) {
    _warn.push(validateRule.required);
  }
  return _warn;
}
function setItem(gmn, _item) {
  if (gmn.type == 'text') {
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
    _item['fullWidth'] = true;
    _item['disabled'] = !!gmn.disabled;
    _item['required'] = !!gmn.required;
    _item['InputProps'] = {
      readOnly: !!gmn.readOnly,
    };
    if (Object.hasOwn(gmn, 'variant')) {
      _item['variant'] = gmn.variant;
    }
    if (Object.hasOwn(gmn, 'multiline')) {
      _item['multiline'] = gmn.multiline;
      _item['rows'] = gmn.rows;
    }
    if (Object.hasOwn(gmn, 'format')) {
      _item['format'] = gmn.format;
    }
  } else if (gmn.type == 'positiveButton' || gmn.type == 'positiveButton2') {
    _item['label'] = gmn.label;
    _item['id'] = gmn.id;
    _item['variant'] = 'contained';
    _item['size'] = gmn.size;
    _item['disabled'] = !!gmn.disabled;
    _item['click'] = gmn.click;
    if (Object.hasOwn(gmn, 'fullWidth') && gmn.fullWidth !== false) {
      _item['fullWidth'] = true;
    }
    if (Object.hasOwn(gmn, 'startIcon')) {
      _item['startIcon'] = gmn.startIcon;
    }
  } else if (gmn.type == 'select') {
    _item['blank'] = gmn.blank;
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
    _item['fullWidth'] = true;
    _item['disabled'] = !!gmn.disabled;
    _item['required'] = !!gmn.required;
    _item['InputProps'] = {
      readOnly: !!gmn.readOnly,
    };
    if (Object.hasOwn(gmn, 'selections')) {
      _item['selections'] = gmn.selections;
    } else {
      _item['selections'] = [];
    }
  } else if (gmn.type == 'checkbox' || gmn.type == 'radiobutton') {
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
    _item['disabled'] = !!gmn.disabled;
  } else if (gmn.type == 'date' || gmn.type == 'month') {
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
    _item['fullWidth'] = true;
    _item['disabled'] = !!gmn.disabled;
    _item['required'] = !!gmn.required;
    _item['InputProps'] = {
      readOnly: !!gmn.readOnly,
    };
    _item['InputLabelProps'] = {
      shrink: true,
    };
  } else if (gmn.type == 'fileDL') {
    _item['key'] = gmn.binding;
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
  } else if (gmn.type == 'link') {
    _item['name'] = gmn.binding;
    _item['label'] = gmn.label;
    _item['fullWidth'] = true;
    _item['click'] = gmn.click;
  } else if (gmn.type == 'simpleLink') {
    _item['name'] = gmn.name;
    _item['text'] = gmn.text;
    _item['fullWidth'] = true;
    _item['click'] = gmn.click;
    _item['gridStyle'] = gmn.gridStyle;
    _item['linkStyle'] = gmn.linkStyle;
  }
  if (gmn.type != 'fileDL') {
    _item['validate'] = setValidate(gmn);
    _item['warn'] = setWarn(gmn);
  }
  _item['readOnly'] = !!gmn.readOnly;
}

/**
   * コンボボックスセット
   * @param {*} mstMap リスト
   * @return {kbnList} 区分リスト
   */
export function getKbnList(mstMap) {
  const list = [];
  const selections = mstMap;
  list.push(<MenuItem value="">&nbsp;</MenuItem>);
  if (mstMap != null) {
    for (const selection of selections) {
      list.push(<MenuItem value={selection.cd}>
        {selection.cdNameAbbr}</MenuItem>);
    }
  }
  return list;
}

/**
 * コンボボックスセット（空白選択項なし）
 * @param {*} mstMap リスト
 * @return {kbnList} 区分リスト
 */
export function getKbnListNoBlank(mstMap) {
  const list = [];
  const selections = mstMap;
  if (mstMap != null) {
    for (const selection of selections) {
      list.push(<MenuItem value={selection.cd}>
        {selection.cdNameAbbr}</MenuItem>);
    }
  } else {
    list.push(<MenuItem value="">&nbsp;</MenuItem>);
  }
  return list;
}
